import type { Transaction } from "@rocicorp/zero";
import { acquireAdvisoryLockIfServer } from "../../drizzle/advisory-lock";
import { zql } from "../../zero/schema";

/**
 * Group namespace boundary for the current grant model and base collaboration plumbing.
 *
 * Product-facing queries and mutators live under `Space`; this namespace
 * keeps the lower-level membership, invite-acceptance, and grant-shape helpers
 * for base collaboration plumbing.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace Group {
  export type WithMembers = {
    members: readonly { userId: string }[];
  };

  export async function createBase(tx: Transaction, { id }: { id: string }): Promise<void> {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.group.insert({ id, timeCreated: timeNow, timeUpdated: timeNow });
  }

  export async function create(
    tx: Transaction,
    { id, memberUserIds }: { id: string; memberUserIds: string[] },
  ): Promise<void> {
    assertValidMemberUserIds(memberUserIds);
    await createBase(tx, { id });
    await Promise.all(memberUserIds.map((userId) => addMember(tx, { groupId: id, userId })));
  }

  function assertValidMemberUserIds(memberUserIds: string[]): void {
    if (memberUserIds.length === 0) {
      throw new Error("Group must have at least one member");
    }
    if (new Set(memberUserIds).size !== memberUserIds.length) {
      throw new Error("Group members must be unique");
    }
  }

  export async function addMember(
    tx: Transaction,
    { groupId, userId }: { groupId: string; userId: string },
  ): Promise<void> {
    await tx.mutate.groupMember.insert({ groupId, userId });
  }

  export async function removeMember(
    tx: Transaction,
    { groupId, userId }: { groupId: string; userId: string },
  ): Promise<void> {
    await tx.mutate.groupMember.delete({ groupId, userId });
  }

  export async function createInvite(
    tx: Transaction,
    args: {
      id: string;
      groupId: string;
      token: string;
      createdById: string;
      maxUses: number;
      useCount: number;
      timeExpires: Temporal.Instant | null;
      timeCreated: Temporal.Instant;
    },
  ): Promise<void> {
    await tx.mutate.groupInvite.insert(args);
  }

  export async function revokeInvite(tx: Transaction, id: string): Promise<void> {
    await tx.mutate.groupInvite.delete({ id });
  }

  export async function requireInviteById(tx: Transaction, id: string) {
    const invite = await tx.run(zql.groupInvite.where("id", "=", id).one());
    if (!invite) {
      throw new Error("Group invite not found");
    }
    return invite;
  }

  export async function requireValidInviteByToken(tx: Transaction, token: string) {
    const now = Temporal.Now.instant();
    const invite = await tx.run(zql.groupInvite.where("token", "=", token).one());
    if (
      !invite ||
      (invite.maxUses !== null && invite.useCount >= invite.maxUses) ||
      (invite.timeExpires !== null && Temporal.Instant.compare(invite.timeExpires, now) <= 0)
    ) {
      throw new Error("Invalid, expired, or exhausted invite");
    }
    return invite;
  }

  export async function acceptInvite(
    tx: Transaction,
    { token, userId, groupId }: { token: string; userId: string; groupId?: string },
  ): Promise<{ groupId: string }> {
    await acquireAdvisoryLockIfServer(tx, "groupInvite", token, "exclusive");
    const invite = await requireValidInviteByToken(tx, token);
    if (groupId !== undefined && invite.groupId !== groupId) {
      throw new Error("Invite does not belong to this group");
    }
    if (await isMember(tx, userId, invite.groupId)) {
      throw new Error("You are already a member of this group");
    }

    await tx.mutate.groupInvite.update({ id: invite.id, useCount: invite.useCount + 1 });
    await addMember(tx, { groupId: invite.groupId, userId });

    return { groupId: invite.groupId };
  }

  export async function isMember(
    tx: Transaction,
    userId: string,
    groupId: string,
  ): Promise<boolean> {
    const member = await tx.run(
      zql.groupMember.where("groupId", "=", groupId).where("userId", "=", userId).one(),
    );
    return !!member;
  }

  export function hasMember(group: WithMembers | null | undefined, userId: string): boolean {
    return group?.members.some((member) => member.userId === userId) === true;
  }

  export async function listMemberUserIds(tx: Transaction, groupId: string): Promise<string[]> {
    const members = await tx.run(zql.groupMember.where("groupId", "=", groupId));
    return members.map((member) => member.userId);
  }
}
